// navModel.ts — Atrium two-tier nav model (Sprint 82, ADR-082).
// ----------------------------------------------------------------------------
// Derives the rail sections + panel sub-pages from the EXISTING menuConfig
// (ALL_MENU_ITEMS + MENU_GROUPS). The prototype's hardcoded ROLES are only a
// visual reference — the real menu items/routes are the source of truth here.
//
// Mapping:
//   menuConfig group (MenuGroupKey) → rail section
//   menuConfig item  (MenuItem)     → panel sub-page
//
// The list of items is already role/feature-filtered by useMenuState before it
// reaches us, so a section only appears when the user has at least one item in
// it. No privilege logic lives here.
import React from 'react';
import {
  HomeOutlined, AppstoreOutlined, TeamOutlined, DollarOutlined,
  ToolOutlined, SettingOutlined, BankOutlined,
} from '@ant-design/icons';
import {
  MENU_GROUPS,
  type MenuItem,
  type MenuGroup,
  type MenuGroupKey,
  type DashboardComponent,
} from '../dashboard/menuConfig';

/** A rail section = one menuConfig group that has at least one visible item. */
export interface AtriumSection {
  key:   MenuGroupKey;
  label: string;
  desc:  string;
  icon:  React.ReactNode;
  /** Sub-pages shown in the context panel for this section. */
  items: MenuItem[];
  /** Pinned to the bottom of the rail (Settings/System). */
  bottom: boolean;
}

// Rail icon per group. Closest AntD equivalents to the prototype's icon map.
const GROUP_ICON: Record<MenuGroupKey, React.ReactNode> = {
  Overview:    <HomeOutlined />,
  'My Home':   <HomeOutlined />,
  Property:    <AppstoreOutlined />,
  Residents:   <TeamOutlined />,
  Finances:    <DollarOutlined />,
  Operations:  <ToolOutlined />,
  System:      <SettingOutlined />,
};

// One-line description under the section title in the context panel.
const GROUP_DESC: Record<MenuGroupKey, string> = {
  Overview:    'Your daily overview',
  'My Home':   'Your unit & account',
  Property:    'Buildings & spaces',
  Residents:   'Owners, board & violations',
  Finances:    'Money in & out',
  Operations:  'Maintenance & comms',
  System:      'Workspace configuration',
};

// Groups pinned to the rail bottom (settings-like).
const BOTTOM_GROUPS = new Set<MenuGroupKey>(['System']);

/**
 * Build the ordered list of rail sections from the (already-filtered) menu
 * items. Group order follows MENU_GROUPS; empty groups are dropped.
 */
export function buildSections(filteredItems: MenuItem[]): AtriumSection[] {
  return MENU_GROUPS
    .map((g: MenuGroup): AtriumSection => ({
      key:    g.key,
      label:  g.label,
      desc:   GROUP_DESC[g.key],
      icon:   GROUP_ICON[g.key] ?? <BankOutlined />,
      items:  filteredItems.filter(i => i.group === g.key),
      bottom: BOTTOM_GROUPS.has(g.key),
    }))
    .filter(s => s.items.length > 0);
}

/** Find which section owns the currently-selected page. */
export function sectionForSelected(
  sections: AtriumSection[],
  selected: DashboardComponent,
): AtriumSection | undefined {
  return (
    sections.find(s => s.items.some(i => i.key === selected)) ?? sections[0]
  );
}
